import React, { useState, useEffect } from 'react';
import { IoLocationOutline, IoCallOutline, IoStatsChartOutline } from 'react-icons/io5';
import CommonLayout from "../../layouts/CommonLayout";
import StaticKakaoMapContainer from "../../containers/StaticKakaoMapContainer";
import { getMyPartnerPlaceInfo } from "../../../api/PartnerPlaceApi";

const PartnerPlaceInfoPage = () => {
    const [partnerPlaceInfo, setPartnerPlaceInfo] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchPartnerPlaceInfo();
    }, []);

    const fetchPartnerPlaceInfo = async () => {
        try {
            const response = await getMyPartnerPlaceInfo();
            setPartnerPlaceInfo(response);
        } catch (error) {
            console.error('제휴매장 정보 조회 실패:', error);
            alert('제휴매장 정보를 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('ko-KR');
    };

    if (loading) {
        return (
            <CommonLayout title="내 제휴 매장 정보">
                <div className="flex justify-center items-center py-12">
                    <div className="text-gray-500">로딩 중...</div>
                </div>
            </CommonLayout>
        );
    }

    if (!partnerPlaceInfo) {
        return (
            <CommonLayout title="내 제휴 매장 정보">
                <div className="flex justify-center items-center py-12">
                    <div className="text-gray-500">제휴매장 정보를 찾을 수 없습니다.</div>
                </div>
            </CommonLayout>
        );
    }

    return (
        <CommonLayout title="내 제휴 매장 정보">
            <div className="p-4 space-y-4 max-w-lg mx-auto">
                {/* 매장 기본 정보 */}
                <div
                    className="rounded-lg border p-4"
                    style={{
                        background: 'linear-gradient(135deg, rgba(0,31,63,0.05) 0%, rgba(0,61,122,0.08) 20%, rgba(0,116,217,0.1) 40%, rgba(57,204,204,0.08) 60%, rgba(46,204,64,0.06) 80%, rgba(61,153,112,0.05) 100%)',
                        borderColor: 'rgba(0,116,217,0.2)'
                    }}
                >
                    <div className="flex items-center mb-3">
                        <span className="text-blue-500 text-sm drop-shadow-sm mr-2">✦</span>
                        <h2 className="text-lg font-semibold">{partnerPlaceInfo.name}</h2>
                    </div>

                    <div className="space-y-3">
                        <div className="flex items-start">
                            <IoLocationOutline className="text-gray-500 mr-2 mt-0.5" size={16} />
                            <span className="text-sm text-gray-600">{partnerPlaceInfo.address}</span>
                        </div>

                        <div className="flex items-center">
                            <IoCallOutline className="text-gray-500 mr-2" size={16} />
                            <span className="text-sm text-gray-600">{partnerPlaceInfo.phone}</span>
                        </div>
                    </div>
                </div>

                {/* 지도 */}
                {partnerPlaceInfo.latitude && partnerPlaceInfo.longitude && (
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <h3 className="text-md font-semibold mb-3 flex items-center">
                            매장 위치
                        </h3>
                        <div className="h-48 rounded-lg overflow-hidden border border-gray-200">
                            <StaticKakaoMapContainer
                                latitude={partnerPlaceInfo.latitude}
                                longitude={partnerPlaceInfo.longitude}
                                level={3}
                            />
                        </div>
                    </div>
                )}

                {/* 제휴 혜택 */}
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h3 className="text-md font-semibold mb-3">제휴 혜택</h3>
                    <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
                        {partnerPlaceInfo.policy}
                    </p>
                </div>

                {/* 매장 이미지 */}
                {partnerPlaceInfo.imageUrl && (
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <h3 className="text-md font-semibold mb-3">매장 소개 사진</h3>
                        <div className="rounded-lg overflow-hidden">
                            <img
                                src={partnerPlaceInfo.imageUrl}
                                alt="매장 사진"
                                className="w-full h-48 object-cover"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                }}
                            />
                            <div
                                className="w-full h-48 bg-gray-100 flex items-center justify-center text-gray-400"
                                style={{ display: 'none' }}
                            >
                                이미지를 불러올 수 없습니다
                            </div>
                        </div>
                    </div>
                )}

                {/* 계약 정보 */}
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h3 className="text-md font-semibold mb-3 flex items-center">
                        계약 정보
                    </h3>
                    <div className="grid grid-cols-1 gap-2">
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <span className="text-sm text-gray-600">총 입금액</span>
                            <span className="text-sm font-medium">{partnerPlaceInfo.cache?.toLocaleString() || 0}원</span>
                        </div>
                        <div className="flex justify-between items-center py-2">
                            <span className="text-sm text-gray-600">제휴 만료일</span>
                            <span className="text-sm font-medium">{formatDateTime(partnerPlaceInfo.until)}</span>
                        </div>
                    </div>
                </div>

                {/* 통계 정보 */}
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h3 className="text-md font-semibold mb-3 flex items-center">
                        <IoStatsChartOutline className="text-gray-600 mr-2" size={18} />
                        통계 정보
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="text-center p-3 bg-blue-50 rounded-lg">
                            <div className="text-lg font-bold text-blue-600">{partnerPlaceInfo.meetingCount || 0}</div>
                            <div className="text-xs text-gray-600">개최된 모임</div>
                        </div>
                        <div className="text-center p-3 bg-green-50 rounded-lg">
                            <div className="text-lg font-bold text-green-600">{partnerPlaceInfo.count || 0}</div>
                            <div className="text-xs text-gray-600 flex items-center justify-center">
                                조회수
                            </div>
                        </div>
                    </div>
                </div>

                {/* 안내 메시지 */}
                <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 leading-relaxed">
                        제휴매장 정보 수정이나 문의사항은 관리자에게 연락해주세요.<br/>
                        이메일: singulecorp@gmail.com<br/>
                        전화번호: 010-5398-8879
                    </p>
                </div>
            </div>
        </CommonLayout>
    );
};

export default PartnerPlaceInfoPage;
